/**
 * An agent's words, with the records it cites by id made readable: each id
 * becomes its number on the case ("record 12", from the record index — the
 * number every screen uses), with its statement on hover, opening the record
 * beside the view. Agents cite what they rest on by id; a reader can't.
 */

import { Fragment, useMemo } from 'react';

import { Anchor, Tooltip } from '@mantine/core';

import type { EvidenceIndexEntry } from '@/types/workbench';
import { recordName, useRecordIndex } from './recordIndex';

const RECORD_ID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

export type TextPart = { text: string } | { id: string };

/** `text` split into plain runs and the records it cites (ids lower-case). */
export function recordParts(text: string): TextPart[] {
  const parts: TextPart[] = [];
  let last = 0;
  for (const match of text.matchAll(RECORD_ID)) {
    const at = match.index ?? 0;
    if (at > last) {
      parts.push({ text: text.slice(last, at) });
    }
    parts.push({ id: match[0].toLowerCase() });
    last = at + match[0].length;
  }
  if (last < text.length) {
    parts.push({ text: text.slice(last) });
  }
  return parts;
}

/** An agent's words as plain text, each record it cites named by its
 * number — where a link can't go (a list's row is itself a button). */
export function agentWords(
  text: string,
  entry: (id: string) => EvidenceIndexEntry | undefined
): string {
  return recordParts(text)
    .map((part) => ('text' in part ? part.text : recordName(entry(part.id))))
    .join('');
}

interface AgentTextProps {
  text: string;
  /** Open a cited record; by default it opens beside the view. */
  onOpenRecord?: (id: string) => void;
}

export function AgentText({ text, onOpenRecord }: Readonly<AgentTextProps>) {
  const parts = useMemo(() => recordParts(text), [text]);
  const records = useRecordIndex();
  const open = onOpenRecord ?? records.open;
  return (
    <>
      {parts.map((part, index) => {
        if ('text' in part) {
          return <Fragment key={index}>{part.text}</Fragment>;
        }
        const entry = records.entry(part.id);
        const label = recordName(entry);
        // Only a record the case holds opens: an id it doesn't is named, no more.
        const shown =
          open && entry ? (
            <Anchor
              component="button"
              type="button"
              inherit
              onClick={(event) => {
                event.stopPropagation();
                open(entry.id);
              }}
              data-testid={`record-link-${part.id}`}
            >
              {label}
            </Anchor>
          ) : (
            <span data-testid={`record-ref-${part.id}`}>{label}</span>
          );
        return (
          <Tooltip
            key={index}
            label={entry?.statement ?? 'Open the record'}
            disabled={!entry}
            multiline
            w={360}
            withArrow
          >
            {shown}
          </Tooltip>
        );
      })}
    </>
  );
}
